import { clampSwingTicks, impactTicks } from "./constants.js";
import { EQUIP_SLOTS } from "./items.js";
import type { EquipSlot, ItemDef, WeaponClass } from "./items.js";
import type { ClassDef, LevelRow } from "./classes.js";
import type { CombatTables } from "./combat.js";

/** The loadout stat line projected by the sim; clients only format it. */
export interface GearCombatStats {
  maxHp: number;
  atkMin: number;
  atkMax: number;
  /** Effective uncharged damage endpoints, after level, gear and proficiency. */
  damageMin: number;
  damageMax: number;
  swing: number;
  impact: number;
  /** Effective accuracy for the equipped weapon (NPCs retain one attack stat). */
  attack: number;
  meleeAttack: number;
  magicAttack: number;
  dmgMod: number;
  meleeDamageBonusPct: number;
  magicDamageBonusPct: number;
  meleeDef: number;
  magicDef: number;
  range: number;
  weaponClass: WeaponClass;
  weaponEfficiencyPct: number;
  meleeWeaponEfficiencyPct: number;
  magicWeaponEfficiencyPct: number;
}

/** Accuracy/damage bonuses select the ATTACK type, not the weapon's delivery. */
export function attackPointsOf(stats: GearCombatStats, kind: WeaponClass): number {
  return kind === "magic" ? stats.magicAttack : stats.meleeAttack;
}

export function damageBonusOf(stats: GearCombatStats, kind: WeaponClass): number {
  return kind === "magic" ? stats.magicDamageBonusPct : stats.meleeDamageBonusPct;
}

/** Sum bonuses first; apply gear + weapon proficiency once to the rolled hit.
 *  Positive multipliers keep a minimum hit of 1. Zero efficiency or a total
 *  damage bonus <= -100 intentionally produces 0 damage. Integer percentages. */
export function scaleGearDamage(raw: number, bonusPct = 0, efficiencyPct = 100): number {
  const factor = Math.max(0, 100 + bonusPct) * efficiencyPct;
  return raw <= 0 || factor === 0 ? 0 : Math.max(1, Math.floor((raw * factor) / 10000));
}

/** Sum floor((a*i+b)/m), i=0..n-1, without walking an authored damage range. */
function floorSum(n: number, m: number, a: number, b: number): number {
  let sum = 0;
  for (;;) {
    if (a >= m) {
      sum += Math.floor((n * (n - 1)) / 2) * Math.floor(a / m);
      a %= m;
    }
    if (b >= m) {
      sum += n * Math.floor(b / m);
      b %= m;
    }
    const top = a * n + b;
    if (top < m) return sum;
    n = Math.floor(top / m);
    b = top % m;
    const oldM = m;
    m = a;
    a = oldM;
  }
}

/** Exact sum of scaled integer outcomes, used by the DPS expected value. */
export function gearDamageSum(min: number, max: number, bonusPct = 0, efficiencyPct = 100): number {
  const factor = Math.max(0, 100 + bonusPct) * efficiencyPct;
  if (factor === 0) return 0;
  const n = max - min + 1;
  const zeros = Math.max(0, Math.min(max, Math.floor(9999 / factor)) - Math.max(1, min) + 1);
  return floorSum(n, 10000, factor, min * factor) + zeros;
}

/** Pure, shared composition. Legacy single attack points seed BOTH buckets
 *  so old gear retains its accuracy until its author tunes the new fields. */
export function deriveGearCombatStats(
  catalog: readonly ItemDef[], equipment: Record<EquipSlot, number | null>,
  combat: CombatTables, levelRow: LevelRow, classDef?: ClassDef,
): GearCombatStats {
  let atkMin = combat.baseAtkMin;
  let atkMax = combat.baseAtkMax;
  let swing = combat.baseSwingTicks;
  let meleeAttack = combat.baseAttack;
  let magicAttack = combat.baseAttack;
  let maxHp = levelRow.maxHp;
  let meleeDef = combat.baseMeleeDef;
  let magicDef = combat.baseMagicDef;
  let meleeDamageBonusPct = 0;
  let magicDamageBonusPct = 0;
  let range = 1;
  let weaponClass: WeaponClass = "melee";
  let hasWeapon = false;
  for (const slot of EQUIP_SLOTS) {
    const def = catalog.find((d) => d.kind === equipment[slot]);
    if (!def) continue;
    const stats = def.stats ?? {};
    if (slot === "weapon") {
      hasWeapon = true;
      weaponClass = def.weaponClass ?? "melee";
      atkMin = stats.atkMin ?? atkMin;
      atkMax = stats.atkMax ?? atkMin;
      swing = stats.speedTicks ?? swing;
      meleeAttack = stats.meleeAttack ?? stats.attack ?? 0;
      magicAttack = stats.magicAttack ?? stats.attack ?? 0;
      range = stats.range ?? range;
    } else {
      maxHp += stats.maxHp ?? 0;
      meleeDef += stats.meleeDef ?? 0;
      magicDef += stats.magicDef ?? 0;
      meleeAttack += stats.meleeAttack ?? 0;
      magicAttack += stats.magicAttack ?? 0;
    }
    meleeDamageBonusPct += stats.meleeDamageBonusPct ?? 0;
    magicDamageBonusPct += stats.magicDamageBonusPct ?? 0;
  }
  meleeAttack = Math.max(0, meleeAttack + levelRow.atkBonus);
  magicAttack = Math.max(0, magicAttack + levelRow.atkBonus);
  swing = clampSwingTicks(swing);
  const meleeWeaponEfficiencyPct = classDef?.meleeWeaponEfficiencyPct ?? 100;
  const magicWeaponEfficiencyPct = classDef?.magicWeaponEfficiencyPct ?? 100;
  const result = {
    maxHp, atkMin, atkMax, swing, impact: impactTicks(swing),
    attack: weaponClass === "magic" ? magicAttack : meleeAttack,
    meleeAttack, magicAttack,
    dmgMod: Math.round((combat.baseDmgMod * levelRow.dmgMod) / 100),
    meleeDamageBonusPct, magicDamageBonusPct, meleeDef, magicDef, range, weaponClass,
    // Bare fists are unchanged; proficiency applies to equipped weapons only.
    weaponEfficiencyPct: !hasWeapon ? 100 : weaponClass === "magic" ? magicWeaponEfficiencyPct : meleeWeaponEfficiencyPct,
    meleeWeaponEfficiencyPct, magicWeaponEfficiencyPct,
  };
  const bonus = weaponClass === "magic" ? magicDamageBonusPct : meleeDamageBonusPct;
  return { ...result,
    damageMin: scaleGearDamage(atkMin, bonus, result.weaponEfficiencyPct),
    damageMax: scaleGearDamage(Math.max(atkMin, Math.floor((atkMax * result.dmgMod) / 100)), bonus, result.weaponEfficiencyPct),
  };
}
